import fileDownload from 'js-file-download'
import React, { useRef, useState } from 'react'

import { FileUploadBox } from '@/components/misc/FileUploadBox'
import Step from '@/components/misc/Step'
import { ToggleMultiple, ToggleOption } from '@/components/misc/ToggleMultiple'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { CloseButton } from '@/components/ui/close-button'
import { ImportReferralsError } from '@/contexts/ImportReferralsContext'
import useAppDispatch from '@/hooks/useAppDispatch'
import useAppSelector from '@/hooks/useAppSelector'
import useImportReferralsContext from '@/hooks/useImportReferralsContext'
import {
  API_PATH_INTERNAL,
  IMPORT_REFERRALS_FILE_NOTIFICATION_DURATION,
} from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import {
  selectReferralsImportHistorical,
  setOnClientDuplicate,
} from '@/redux/slices/referralsImportHistorical'
import { selectCurrentCompanyOrFail } from '@/redux/slices/session'
import { FileUploadBoxState } from '@/types/misc'
import { BulkImportReferralCollection } from '@/types/responses/referral-import'

interface Props {
  onSuccessfulUpload: (data: BulkImportReferralCollection) => void
  className?: string
}

const ImportReferralsPrompt: React.FC<Props> = ({
  onSuccessfulUpload,
  className,
}) => {
  const dispatch = useAppDispatch()
  const currentCompany = useAppSelector(selectCurrentCompanyOrFail)
  const {
    uploadStatus,
    uploadError,
    setFile,
    setUploadStatus,
    setUploadError,
  } = useImportReferralsContext()
  const referralsImportHistoricalState = useAppSelector(
    selectReferralsImportHistorical,
  )
  const [isErrorMessageExpanded, setIsErrorMessageExpanded] = useState(false)
  const inputFileRef = useRef<HTMLInputElement>(null)

  let uploadBoxMessage = 'Click to upload or drag and drop'
  let uploadBoxState: FileUploadBoxState = 'enabled'
  if (uploadStatus === 'uploading') {
    uploadBoxState = 'uploading'
    uploadBoxMessage = 'Uploading...'
  } else if (uploadStatus === 'success') {
    uploadBoxState = 'success'
    uploadBoxMessage = 'Data has been uploaded successfully'
  } else if (uploadStatus === 'error') {
    uploadBoxState = 'error'
    uploadBoxMessage = 'Upload failed'
  }

  async function handleTemplateDownload() {
    const fileName = 'import-historical-referrals-template.xlsx'
    const response = await apiService.get(
      `${API_PATH_INTERNAL}/companies/${currentCompany.id}/download-import-historical-referrals-template`,
      {
        responseType: 'blob',
      },
    )
    fileDownload(response.data, fileName)
  }

  function handleDuplicateOptionChange(value: string) {
    dispatch(
      setOnClientDuplicate(value === 'true' ? 'update' : 'keep-original'),
    )
  }
  function handleFileCancel() {
    setFile(null)

    if (inputFileRef.current?.value) {
      inputFileRef.current.value = ''
    }

    setUploadStatus('idle')
  }
  async function handleUpload(file: File) {
    if (!file) {
      return
    }

    const formData = new FormData()
    formData.append('file', file)

    setFile(file)
    setUploadStatus('uploading')
    setUploadError(null)
    setIsErrorMessageExpanded(false)

    apiService
      .post(
        `${API_PATH_INTERNAL}/companies/${currentCompany.id}/process-import-historical-referrals`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      .then((response) => {
        handleFileCancel()
        setUploadStatus('success')

        setTimeout(() => {
          setUploadStatus('idle')
        }, IMPORT_REFERRALS_FILE_NOTIFICATION_DURATION)

        onSuccessfulUpload(response.data)
      })
      .catch((error) => {
        setUploadStatus('error')

        const uploadError: ImportReferralsError = {
          message: error.response.data['hydra:description'] ?? '',
          fileName: file.name,
        }

        if (uploadError.message === '') {
          uploadError.message = error.response.statusText ?? 'Unknown error'
        }

        setUploadError(uploadError)
        setIsErrorMessageExpanded(true)

        setTimeout(() => {
          handleFileCancel()
        }, IMPORT_REFERRALS_FILE_NOTIFICATION_DURATION)
      })
  }

  return (
    <div className={className}>
      <Step step="1" className="mt-0">
        <p className="mb-2">
          Download our template spreadsheet and fill in your historical
          referrals.
        </p>
        <Button onClick={() => handleTemplateDownload()}>
          Download template
        </Button>
      </Step>

      <Step step="2">
        <p className="mb-2">
          Upload the completed spreadsheet as a Microsoft Excel (.xlsx).
        </p>

        <p className="mb-2">
          If your import contains duplicated clients, RQ should:
        </p>

        <ToggleMultiple
          options={overwriteDuplicatesOptions}
          value={
            referralsImportHistoricalState.onClientDuplicate == 'update'
              ? 'true'
              : 'false'
          }
          onChange={handleDuplicateOptionChange}
          enabled={uploadBoxState === 'enabled'}
          className="mb-4"
        />

        <FileUploadBox
          state={uploadBoxState}
          onChange={handleUpload}
          message={uploadBoxMessage}
          accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          inputRef={inputFileRef}
        />

        {uploadError && isErrorMessageExpanded && (
          <Alert className="my-4" variant="destructive">
            <AlertTitle className="flex justify-between gap-2">
              There was a problem with importing referrals from
              <br />"{uploadError.fileName}" file:
              <CloseButton onClick={() => setIsErrorMessageExpanded(false)} />
            </AlertTitle>

            <AlertDescription>
              {uploadError.message.split('\n').map((line, index) => (
                <p key={index} className="my-1">
                  {line}
                </p>
              ))}
            </AlertDescription>
          </Alert>
        )}

        {uploadError && !isErrorMessageExpanded && (
          <div className="my-4 justify-end">
            <Button
              variant="plain"
              onClick={() => setIsErrorMessageExpanded(true)}
            >
              Show last error
            </Button>
          </div>
        )}
      </Step>

      <Step step="3">
        <p className="mb-2">Review import data.</p>
      </Step>

      <Step step="4">
        <p className="mb-2">Import referrals.</p>
      </Step>
    </div>
  )
}

const overwriteDuplicatesOptions: ToggleOption[] = [
  {
    label: (
      <p className="m-0">
        <b>Update</b> with imported data.
      </p>
    ),
    value: 'true',
  },
  {
    label: (
      <p className="m-0">
        <b>Keep existing</b> data.
      </p>
    ),
    value: 'false',
  },
]

export default ImportReferralsPrompt
